type UnknownObject = Record<string, unknown>

const CELEX_DOCUMENT_ID_PATTERN = /^\d{5}[A-Z]\d{4,}(?:-\d{8})?$/

/** Matches EU CELEX-style ids in prose (same shape as {@link buildEurLexHtmlUrlFromCelex}). */
const BARE_CELEX_IN_MARKDOWN = /\b(\d{5}[A-Z]\d{4,}(?:-\d{8})?)\b/g

const MARKDOWN_LINK_CHUNK = /(\[[^\]]*\]\([^)]*\))/g

const EUR_LEX_MARKDOWN_LINK =
  /\[([^\]]*)\]\((https?:\/\/(?:www\.)?eur-lex\.europa\.eu[^)]*)\)/gi
const ANNEX_CITATION_LABEL_PATTERN = /^(annex|appendix)\b/i

export type RegulationSnippet = {
  celex: string
  consolidatedCelex: string | null
  chunkText: string
  evidencePreview: string | null
  /** Instrument title from Radar when available. */
  instrumentTitle: string | null
  /** Radar document id for canonical block rendering. */
  documentId: string | null
  /** Inclusive start block id of the cited chunk span. */
  startBlockId: string | null
  /** Inclusive end block id of the cited chunk span. */
  endBlockId: string | null
  /** Retrieval rank from Radar when returned by the tool. */
  rank: number | null
  /** Result order in the chunk set (not canonical block order). */
  chunkOrder: number | null
}

export type RegulationSnippetsOutput = {
  items: RegulationSnippet[]
}

export type CelexScopeSnippet = {
  celex: string
  consolidatedCelex: string | null
  evidencePreview: string | null
  /** Instrument title from Radar (`rag_documents`) when available. */
  instrumentTitle: string | null
}

export type CelexScopesOutput = {
  items: CelexScopeSnippet[]
}

export type TrustedWebSourceItem = {
  title: string
  url: string
  sourceDisplayName: string
  snippet: string
}

export type TrustedWebSourceCatalogEntry = {
  displayName: string
  domain: string
}

export type TrustedWebSourcesOutput = {
  items: TrustedWebSourceItem[]
  searchedSources: TrustedWebSourceCatalogEntry[]
  failed?: boolean
}

export type ProductRecommendationField = {
  field: 'name' | 'description' | 'yearOfManufacture' | 'productModel'
  currentValue: string | number | null
  proposedValue: string | number
}

export type ProductRecommendationToolItem = {
  id: string
  kind: 'UPDATE_METADATA'
  reason: 'empty' | 'inconsistent'
  confidence?: 'low' | 'medium' | 'high'
  fields: ProductRecommendationField[]
}

export type SuggestProductRecommendationsOutput = {
  recommendations: ProductRecommendationToolItem[]
}

export type PersistedInferredFactToolItem = {
  id: string
  label: string
  value: string
  confidence?: 'low' | 'medium' | 'high'
  rationale?: string
}

export type RecordProductContextOutput = {
  recommendations: ProductRecommendationToolItem[]
  persistedFacts: PersistedInferredFactToolItem[]
}

export function parsePersistedInferredFactsOutput(
  output: unknown
): PersistedInferredFactToolItem[] {
  const outputObject = asObject(output)
  if (!outputObject) {
    return []
  }

  if (Array.isArray(outputObject.facts)) {
    return parsePersistedInferredFactsArray(outputObject.facts)
  }
  if (Array.isArray(outputObject.persistedFacts)) {
    return parsePersistedInferredFactsArray(outputObject.persistedFacts)
  }
  return []
}

function parsePersistedInferredFactsArray(
  facts: unknown[]
): PersistedInferredFactToolItem[] {
  const parsed: PersistedInferredFactToolItem[] = []
  for (const factValue of facts) {
    const factObject = asObject(factValue)
    if (!factObject) {
      continue
    }
    const factId = asNonEmptyString(factObject.id)
    const label = asNonEmptyString(factObject.label)
    const value = asNonEmptyString(factObject.value)
    if (!factId || !label || !value) {
      continue
    }
    const confidenceValue = factObject.confidence
    if (
      confidenceValue !== undefined &&
      confidenceValue !== null &&
      confidenceValue !== 'low' &&
      confidenceValue !== 'medium' &&
      confidenceValue !== 'high'
    ) {
      continue
    }
    const rationale = asNonEmptyString(factObject.rationale)
    parsed.push({
      id: factId,
      label,
      value,
      ...(confidenceValue !== undefined &&
        confidenceValue !== null && { confidence: confidenceValue }),
      ...(rationale !== null && { rationale }),
    })
  }
  return parsed
}

export function parseRecordProductContextOutput(
  output: unknown
): RecordProductContextOutput | null {
  const outputObject = asObject(output)
  if (!outputObject) {
    return null
  }

  const recommendations =
    parseSuggestProductRecommendationsOutput(output)?.recommendations ?? []
  const persistedFacts = parsePersistedInferredFactsOutput(output)

  if (recommendations.length === 0 && persistedFacts.length === 0) {
    return null
  }

  return {
    recommendations,
    persistedFacts,
  }
}

export type LegislationCitationTarget = {
  celex: string
  documentId: string
  startBlockId: string
  endBlockId: string
  articleLabel?: string
  /** Grounded chunk text from RAG retrieval; used for highlight fallback only. */
  chunkText?: string
}

export function toLegislationCitationTarget(
  input: Pick<
    RegulationSnippet,
    'celex' | 'documentId' | 'startBlockId' | 'endBlockId' | 'chunkText'
  > & {
    articleLabel?: string
  }
): LegislationCitationTarget | null {
  if (!input.documentId || !input.startBlockId || !input.endBlockId) {
    return null
  }
  return {
    celex: input.celex,
    documentId: input.documentId,
    startBlockId: input.startBlockId,
    endBlockId: input.endBlockId,
    chunkText: input.chunkText,
    ...(input.articleLabel != null ? { articleLabel: input.articleLabel } : {}),
  }
}

export function toLegislationCitationTargetFromCelexLookup(
  input: Pick<
    LegislationCitationTarget,
    'celex' | 'documentId' | 'startBlockId' | 'endBlockId'
  > & {
    articleLabel?: string
  }
): LegislationCitationTarget {
  return {
    celex: input.celex,
    documentId: input.documentId,
    startBlockId: input.startBlockId,
    endBlockId: input.endBlockId,
    ...(input.articleLabel != null && input.articleLabel.trim().length > 0
      ? { articleLabel: input.articleLabel.trim() }
      : {}),
  }
}

export function extractCelexFromCitationHref(href: string): string | null {
  const internal = parseInternalLegislationViewerUrl(href)
  if (internal?.celex) {
    return internal.celex
  }
  return extractCelexFromEurLexUrl(href)
}

export function isAnnexCitationLabel(label: string): boolean {
  return ANNEX_CITATION_LABEL_PATTERN.test(label.trim())
}

function asObject(value: unknown): UnknownObject | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }
  return value as UnknownObject
}

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  if (trimmed.length === 0) {
    return null
  }
  return trimmed
}

function asInteger(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number.parseInt(value, 10)
    return Number.isInteger(parsed) ? parsed : null
  }
  return null
}

export function parseSuggestProductRecommendationsOutput(
  output: unknown
): SuggestProductRecommendationsOutput | null {
  const outputObject = asObject(output)
  if (!outputObject || !Array.isArray(outputObject.recommendations)) {
    return null
  }

  const recommendations: ProductRecommendationToolItem[] = []
  for (const recommendation of outputObject.recommendations) {
    const recommendationObject = asObject(recommendation)
    if (!recommendationObject) {
      continue
    }
    const recommendationId = asNonEmptyString(recommendationObject.id)
    const recommendationKind = recommendationObject.kind
    const recommendationReason = recommendationObject.reason
    if (
      !recommendationId ||
      recommendationKind !== 'UPDATE_METADATA' ||
      (recommendationReason !== 'empty' &&
        recommendationReason !== 'inconsistent')
    ) {
      continue
    }
    const confidenceValue = recommendationObject.confidence
    if (
      confidenceValue !== undefined &&
      confidenceValue !== null &&
      confidenceValue !== 'low' &&
      confidenceValue !== 'medium' &&
      confidenceValue !== 'high'
    ) {
      continue
    }
    if (!Array.isArray(recommendationObject.fields)) {
      continue
    }

    const fields: ProductRecommendationField[] = []
    for (const fieldValue of recommendationObject.fields) {
      const fieldObject = asObject(fieldValue)
      if (!fieldObject) {
        continue
      }
      const fieldName = fieldObject.field
      if (
        fieldName !== 'name' &&
        fieldName !== 'description' &&
        fieldName !== 'yearOfManufacture' &&
        fieldName !== 'productModel'
      ) {
        continue
      }
      const currentValue = fieldObject.currentValue
      const proposedValue = fieldObject.proposedValue
      if (
        currentValue !== null &&
        typeof currentValue !== 'string' &&
        typeof currentValue !== 'number'
      ) {
        continue
      }
      if (typeof proposedValue !== 'string' && typeof proposedValue !== 'number') {
        continue
      }
      fields.push({
        field: fieldName,
        currentValue,
        proposedValue,
      })
    }

    if (fields.length === 0) {
      continue
    }

    recommendations.push({
      id: recommendationId,
      kind: 'UPDATE_METADATA',
      reason: recommendationReason,
      ...(confidenceValue !== undefined &&
        confidenceValue !== null && { confidence: confidenceValue }),
      fields,
    })
  }

  return { recommendations }
}

function normalizeCelex(value: unknown): string | null {
  const celexText = asNonEmptyString(value)
  if (!celexText) {
    return null
  }
  const candidate = celexText
    .split('__')
    .map((part) => part.trim().toUpperCase())
    .find((part) => part.length > 0)
  if (!candidate) {
    return null
  }
  return candidate
}

export function parseSearchRadarRagChunksOutput(
  output: unknown
): RegulationSnippetsOutput | null {
  const outputObject = asObject(output)
  if (!outputObject || !Array.isArray(outputObject.items)) {
    return null
  }

  const items: RegulationSnippet[] = []
  for (const item of outputObject.items) {
    const itemObject = asObject(item)
    if (!itemObject) {
      continue
    }
    const celex = normalizeCelex(itemObject.celex)
    const chunkText = asNonEmptyString(itemObject.chunkText)
    if (!celex || !chunkText) {
      continue
    }
    const evidencePreview = asNonEmptyString(itemObject.evidencePreview)
    const instrumentTitle = asNonEmptyString(itemObject.instrumentTitle)
    const documentId = asNonEmptyString(itemObject.documentId)
    const startBlockId = asNonEmptyString(itemObject.startBlockId)
    const endBlockId = asNonEmptyString(itemObject.endBlockId)
    const rank = asInteger(itemObject.rank)
    const chunkOrder = asInteger(itemObject.chunkOrder)
    const consolidatedCelex = normalizeCelex(itemObject.consolidatedCelex)
    items.push({
      celex,
      consolidatedCelex,
      chunkText,
      evidencePreview,
      instrumentTitle,
      documentId,
      startBlockId,
      endBlockId,
      rank,
      chunkOrder,
    })
  }

  return { items }
}

const REGULATION_PREVIEW_UI_MAX_CHARS = 200

/** Single-line style preview for tool cards (not full RAG chunk body). */
export function truncatePlainTextForUi(text: string, maxChars: number): string {
  const normalized = text.trim().replace(/\s+/g, ' ')
  if (normalized.length <= maxChars) {
    return normalized
  }
  return `${normalized.slice(0, maxChars).trimEnd()}…`
}

export function regulationSnippetPreviewForUi(item: RegulationSnippet): string {
  const primary = item.evidencePreview ?? item.chunkText
  return truncatePlainTextForUi(primary, REGULATION_PREVIEW_UI_MAX_CHARS)
}

export function parseSearchRadarCelexScopesOutput(
  output: unknown
): CelexScopesOutput | null {
  const outputObject = asObject(output)
  if (!outputObject || !Array.isArray(outputObject.items)) {
    return null
  }

  const items: CelexScopeSnippet[] = []
  for (const item of outputObject.items) {
    const itemObject = asObject(item)
    if (!itemObject) {
      continue
    }
    const celex = normalizeCelex(itemObject.celex)
    if (!celex) {
      continue
    }
    const evidencePreview = asNonEmptyString(itemObject.evidencePreview)
    const instrumentTitle = asNonEmptyString(itemObject.instrumentTitle)
    const consolidatedCelex = normalizeCelex(itemObject.consolidatedCelex)
    items.push({ celex, consolidatedCelex, evidencePreview, instrumentTitle })
  }

  return { items }
}

export function parseSearchTrustedWebSourcesOutput(
  output: unknown
): TrustedWebSourcesOutput | null {
  const outputObject = asObject(output)
  if (!outputObject) {
    return null
  }

  const items: TrustedWebSourceItem[] = []
  if (Array.isArray(outputObject.items)) {
    for (const item of outputObject.items) {
      const itemObject = asObject(item)
      if (!itemObject) {
        continue
      }
      const title = asNonEmptyString(itemObject.title)
      const url = asNonEmptyString(itemObject.url)
      const sourceDisplayName = asNonEmptyString(itemObject.sourceDisplayName)
      const snippet = asNonEmptyString(itemObject.snippet)
      if (!title || !url || !sourceDisplayName || !snippet) {
        continue
      }
      let parsedUrl: URL
      try {
        parsedUrl = new URL(url)
      } catch {
        continue
      }
      if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
        continue
      }
      items.push({ title, url, sourceDisplayName, snippet })
    }
  }

  const searchedSources: TrustedWebSourceCatalogEntry[] = []
  if (Array.isArray(outputObject.searchedSources)) {
    for (const source of outputObject.searchedSources) {
      const sourceObject = asObject(source)
      if (!sourceObject) {
        continue
      }
      const displayName = asNonEmptyString(sourceObject.displayName)
      const domain = asNonEmptyString(sourceObject.domain)
      if (!displayName || !domain) {
        continue
      }
      searchedSources.push({ displayName, domain })
    }
  }

  if (items.length === 0 && searchedSources.length === 0) {
    return null
  }

  const metadataObject = asObject(outputObject.metadata)
  const failed = metadataObject?.failed === true

  return { items, searchedSources, ...(failed ? { failed: true } : {}) }
}

export function resolveEurLexCelexForLinks(input: {
  celex: string
  consolidatedCelex?: string | null
}): string {
  if (
    input.consolidatedCelex &&
    CELEX_DOCUMENT_ID_PATTERN.test(input.consolidatedCelex)
  ) {
    return input.consolidatedCelex
  }
  return input.celex
}

export function buildEurLexUrlFromCelex(celex: string): string | null {
  if (!CELEX_DOCUMENT_ID_PATTERN.test(celex)) {
    return null
  }
  return `https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:${encodeURIComponent(celex)}`
}

export function buildEurLexHtmlUrlFromCelex(celex: string): string | null {
  if (!CELEX_DOCUMENT_ID_PATTERN.test(celex)) {
    return null
  }
  return `https://eur-lex.europa.eu/legal-content/EN/TXT/HTML/?uri=CELEX:${encodeURIComponent(celex)}`
}

const INTERNAL_LEGISLATION_VIEWER_PATH =
  /^\/lex\/([A-Z0-9]+)\/([^/]+)\/([^/]+)\/?$/i

export function buildInternalLegislationViewerUrl(input: {
  celex: string
  startBlockId: string
  endBlockId: string
  origin?: string
}): string | null {
  const celex = normalizeCelex(input.celex)
  const startBlockId = asNonEmptyString(input.startBlockId)
  const endBlockId = asNonEmptyString(input.endBlockId)
  if (!celex || !startBlockId || !endBlockId) {
    return null
  }
  if (!CELEX_DOCUMENT_ID_PATTERN.test(celex)) {
    return null
  }
  const origin = input.origin?.trim()
  const normalizedOrigin = origin ? origin.replace(/\/+$/, '') : ''
  const basePath = `${normalizedOrigin}/lex`
  return `${basePath}/${encodeURIComponent(celex)}/${encodeURIComponent(startBlockId)}/${encodeURIComponent(endBlockId)}`
}

export function parseInternalLegislationViewerUrl(input: string): {
  celex: string
  startBlockId: string
  endBlockId: string
} | null {
  try {
    const baseOrigin =
      typeof window !== 'undefined' && window.location.origin
        ? window.location.origin
        : 'http://localhost'
    const parsedUrl = new URL(input, baseOrigin)
    const match = INTERNAL_LEGISLATION_VIEWER_PATH.exec(parsedUrl.pathname)
    if (!match) {
      return null
    }
    const celex = normalizeCelex(match[1])
    const startBlockId = asNonEmptyString(decodeURIComponent(match[2]))
    const endBlockId = asNonEmptyString(decodeURIComponent(match[3]))
    if (!celex || !startBlockId || !endBlockId) {
      return null
    }
    if (!CELEX_DOCUMENT_ID_PATTERN.test(celex)) {
      return null
    }
    return {
      celex,
      startBlockId,
      endBlockId,
    }
  } catch {
    return null
  }
}

/**
 * When plain text contains a raw `http(s)://...` URL, bare CELEX ids in the query
 * (e.g. `?uri=CELEX:32006L0042`) must not get a second markdown link. Prose like
 * `CELEX:32006L0042` has no scheme before the id, so we still auto-link.
 */
function isBareCelexInsidePlainHttpUrl(beforeSlice: string): boolean {
  const lastHttps = beforeSlice.lastIndexOf('https://')
  const lastHttp = beforeSlice.lastIndexOf('http://')
  const schemeStart = Math.max(lastHttps, lastHttp)
  if (schemeStart === -1) {
    return false
  }
  const afterScheme = beforeSlice.slice(schemeStart)
  if (/\s/.test(afterScheme)) {
    return false
  }
  return true
}

export function extractCelexFromEurLexUrl(rawUrl: string): string | null {
  try {
    const decoded = decodeURIComponent(rawUrl.replace(/\+/g, ' '))
    const match = /CELEX:([A-Z0-9]+)/i.exec(decoded)
    if (!match) {
      return null
    }
    const candidate = match[1].trim().toUpperCase()
    if (!CELEX_DOCUMENT_ID_PATTERN.test(candidate)) {
      return null
    }
    return candidate
  } catch {
    return null
  }
}

function linkBareCelexInPlainSegment(segment: string): string {
  return segment.replace(
    BARE_CELEX_IN_MARKDOWN,
    (full: string, celex: string, offset: number) => {
      const beforeSlice = segment.slice(0, offset)
      const tail = beforeSlice.slice(-40)
      if (isBareCelexInsidePlainHttpUrl(beforeSlice)) {
        return full
      }
      if (/CELEX%3A/i.test(tail)) {
        return full
      }
      const url = buildEurLexHtmlUrlFromCelex(celex)
      if (!url) {
        return full
      }
      return `[${celex}](${url})`
    }
  )
}

/**
 * Wraps bare CELEX tokens in EUR-Lex markdown links. Leaves existing `[text](url)` chunks unchanged.
 */
export function autoLinkCelexInMarkdown(text: string): string {
  const parts = text.split(MARKDOWN_LINK_CHUNK)
  return parts
    .map((part) => {
      if (/^\[[^\]]*\]\([^)]*\)$/.test(part)) {
        return part
      }
      return linkBareCelexInPlainSegment(part)
    })
    .join('')
}

/**
 * Removes markdown links to EUR-Lex when the URL does not contain a valid CELEX id (anti-hallucination).
 * Valid links are kept unchanged.
 */
export function sanitizeCelexLinksInMarkdown(text: string): string {
  return text.replace(
    EUR_LEX_MARKDOWN_LINK,
    (full: string, label: string, url: string) => {
      const celex = extractCelexFromEurLexUrl(url)
      if (!celex) {
        return label
      }
      if (!buildEurLexHtmlUrlFromCelex(celex)) {
        return label
      }
      return full
    }
  )
}
